import React from 'react';
import { Calculator, ChartLineUp, Buildings, UsersThree, ArrowRight, Star, X, CheckCircle, Clock, TrendUp, ShieldCheck, Briefcase } from '@phosphor-icons/react';
import { usePersona, PersonaId, personas } from './PersonaContext';
interface Service {
  id: Exclude<PersonaId, null> | 'va';
  title: string;
  tagline: string;
  icon: typeof Calculator;
  bullets: string[];
  badge?: string;
  personaTag: Exclude<PersonaId, null> | null;
}
const services: Service[] = [{
  id: 'sme',
  title: 'Bookkeeping and Payroll',
  tagline: 'Day-to-day books and payroll, supervised by an IPA-recognised manager who thinks like a CFO.',
  icon: Calculator,
  bullets: ['Financial statement reconciliation', 'Double-entry bookkeeping', 'Payroll processing and STP', 'Accounts payable and receivable', 'BAS filing and compliance', 'Monthly management reporting included'],
  badge: 'Includes CFO-level supervision',
  personaTag: 'sme'
}, {
  id: 'cfo',
  title: 'Virtual CFO',
  tagline: 'Full strategic financial leadership, without a full-time hire.',
  icon: ChartLineUp,
  bullets: ['Financial strategy and decision support', 'Budgeting and rolling forecasts', 'Risk management and cash-flow modelling', 'Investment and funding support'],
  personaTag: 'cfo'
}, {
  id: 'accountant',
  title: 'White-Label for Accounting Firms',
  tagline: 'Bespoke bookkeeping inside your stack, under your brand.',
  icon: Buildings,
  bullets: ['White-labelled or collaborative support', 'Workpapers and reconciliations', 'Client-facing or back-office only', 'Aligned with your workflow and tech stack'],
  personaTag: 'accountant'
}, {
  id: 'va',
  title: 'Virtual Employee Services',
  tagline: 'Scalable back-office team support, managed and process-driven.',
  icon: UsersThree,
  bullets: ['Back-office team support', 'Scalable staffing assistance', 'Process-driven delivery', 'Managed support structure'],
  personaTag: null
}];
const comparison = [{
  typical: 'Data entry only. Reconciles transactions, runs payroll.',
  knight: 'Reconciles, runs payroll, and watches your numbers like a CFO.'
}, {
  typical: 'You only hear from them at BAS time.',
  knight: 'Monthly management reporting and proactive cash-flow flags.'
}, {
  typical: 'One operator. Gone when they take leave.',
  knight: 'A team and an IPA-recognised supervising manager, always.'
}, {
  typical: 'Cheap, but you still need to hire a CFO later.',
  knight: 'Books plus CFO-level thinking from day one, on a fixed monthly fee.'
}];
const outcomes = [{
  title: 'Get your week back',
  desc: 'We take the recurring bookkeeping, payroll and admin off your plate. Your internal team stops drowning in compliance.',
  icon: Clock
}, {
  title: 'Walk in with current numbers',
  desc: 'Monthly management reports, cash-flow visibility and BAS-ready books. Not numbers that are 6 weeks old.',
  icon: TrendUp
}, {
  title: 'Sleep at night on payroll',
  desc: 'Awards, leave, STP and superannuation handled by an IPA-supervised team. No more pay-run dread.',
  icon: ShieldCheck
}];
const accountantModels = ['Referral / 90/10 split', 'Recruit and Place / From $500 AUD/mo', 'White Label Lift Off / From $3,000 AUD/mo + staffing', 'White Label Partner / From $5,000 AUD/mo + staffing'];
export function Services() {
  const {
    persona
  } = usePersona();
  const sorted = persona ? [...services].sort((a, b) => a.personaTag === persona ? -1 : b.personaTag === persona ? 1 : 0) : services;
  return <section id="services" className="py-16 lg:py-24 bg-off-white" data-id="element-359">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-360">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-361">
          <p className="font-mono text-[11px] tracking-wider uppercase text-knight-orange mb-3" data-id="element-362">
            What you get
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-charcoal leading-tight mb-5" data-id="element-363">
            Bookkeeping, payroll and CFO support, under one roof
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed" data-id="element-364">
            Most providers do one or the other. Knight is built so day-to-day
            bookkeeping and payroll come with CFO-level supervision included,
            and full Virtual CFO support is there when you are ready to step up.
          </p>
        </div>

        {persona && <div className="max-w-4xl mx-auto mb-10 bg-near-black rounded-md border border-hairline-dark p-6 lg:p-8 relative overflow-hidden transition-all duration-500 ease-out-expo" data-id="element-365">
            <div className="absolute inset-0 bg-grid-pattern opacity-20 pointer-events-none" data-id="element-366"></div>
            <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-4" data-id="element-367">
              <div className="flex-grow" data-id="element-368">
                <div className="inline-flex items-center px-2 py-0.5 rounded bg-knight-orange/15 mb-3" data-id="element-369">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-knight-orange" data-id="element-370">
                    For / {personas[persona].shortLabel}
                  </span>
                </div>
                <h3 className="text-xl lg:text-2xl font-heading font-bold text-white leading-snug mb-2" data-id="element-371">
                  {personas[persona].primaryService}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed" data-id="element-372">
                  Based on your selection above. We have moved it to the top of
                  the list and pre-filled the form at the bottom of the page.
                </p>
              </div>
            </div>
          </div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16 stagger-children" data-id="element-373">
          {sorted.map(service => {
          const isPersonaMatch = persona && service.personaTag === persona;
          const isFeatured = !!service.badge || isPersonaMatch;
          return <div key={service.id} className={`relative bg-white p-7 lg:p-8 rounded-md border transition-all duration-500 ease-out-expo flex flex-col h-full ${isPersonaMatch ? 'border-knight-orange' : service.badge ? 'border-knight-orange/30' : 'border-hairline'}`} data-id="element-374">
                {(service.badge || isPersonaMatch) && <div className="absolute -top-2.5 left-7 bg-knight-orange text-white font-mono text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded flex items-center gap-1" data-id="element-375">
                    <Star size={10} weight="fill" data-id="element-376" />
                    {isPersonaMatch ? 'Recommended for you' : service.badge}
                  </div>}
                <div className="w-11 h-11 bg-off-white rounded-md flex items-center justify-center mb-5 border border-hairline" data-id="element-377">
                  <service.icon size={22} weight="duotone" className="text-knight-orange" data-id="element-378" />
                </div>
                <h3 className="text-xl font-heading font-bold text-charcoal mb-2" data-id="element-379">
                  {service.title}
                </h3>
                <p className="text-sm text-gray-500 mb-5" data-id="element-380">{service.tagline}</p>
                <ul className="space-y-2.5 mb-7 flex-grow" data-id="element-381">
                  {service.bullets.map((bullet, j) => <li key={j} className="flex items-start text-[15px] text-gray-700" data-id="element-382">
                      <span className="w-1 h-1 rounded-full bg-knight-orange mt-2.5 mr-3 flex-shrink-0" data-id="element-383"></span>
                      {bullet}
                    </li>)}
                </ul>
                <a href="#lead" className="inline-flex items-center text-knight-orange font-semibold text-sm hover:text-knight-orange-hover transition-colors mt-auto" data-id="element-384">
                  Discuss this service
                  <ArrowRight size={14} weight="regular" className="ml-1.5" data-id="element-385" />
                </a>
              </div>;
        })}
        </div>

        {/* Accountant White-Label Block */}
        {persona === 'accountant' && <div className="max-w-5xl mx-auto bg-near-black rounded-md border border-hairline-dark p-8 lg:p-10 mb-16 relative overflow-hidden" data-id="element-386">
            <div className="absolute inset-0 bg-grid-pattern opacity-20 pointer-events-none" data-id="element-387"></div>
            <div className="relative z-10" data-id="element-388">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-knight-orange/10 border border-knight-orange/20 mb-5" data-id="element-389">
                <Briefcase size={14} weight="duotone" className="text-knight-orange mr-2" data-id="element-390" />
                <span className="font-mono text-[10px] font-semibold text-knight-orange tracking-wider uppercase" data-id="element-391">
                  For accounting firms
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-heading font-bold text-white leading-tight mb-4" data-id="element-392">
                Scale your firm without scaling your headcount.
              </h3>
              <p className="text-base text-gray-400 leading-relaxed mb-8 max-w-2xl" data-id="element-393">
                Knight acts as a seamless extension of your team.
                White-labelled, collaborative, client-facing or fully
                back-office. Four engagement models, transparent pricing.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 mb-8" data-id="element-394">
                {accountantModels.map((m, i) => <div key={i} className="flex items-start gap-2 text-sm text-gray-300" data-id="element-395">
                    <CheckCircle size={16} weight="regular" className="text-knight-orange flex-shrink-0 mt-0.5" data-id="element-396" />
                    {m}
                  </div>)}
              </div>

              <a href="#lead" className="inline-flex items-center px-6 py-3.5 bg-knight-orange text-white font-semibold rounded-md hover:bg-knight-orange-hover transition-colors duration-300 ease-out-expo text-sm" data-id="element-397">
                Talk to Knight about firm support
                <ArrowRight size={16} weight="regular" className="ml-2" data-id="element-398" />
              </a>
            </div>
          </div>}

        {/* Comparison Block */}
        <div className="max-w-5xl mx-auto mb-12" data-id="element-399">
          <p className="font-mono text-[11px] tracking-wider uppercase text-knight-orange mb-3 text-center" data-id="element-400">
            Why Knight
          </p>
          <h3 className="text-2xl sm:text-3xl font-heading font-bold text-charcoal leading-tight mb-8 text-center" data-id="element-401">
            Most bookkeepers tick boxes. Ours think like CFOs.
          </h3>

          <div className="rounded-md border border-hairline overflow-hidden" data-id="element-402">
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-403">
              <div className="p-7 lg:p-9 bg-white border-b md:border-b-0 md:border-r border-hairline" data-id="element-404">
                <div className="flex items-center gap-2 mb-6" data-id="element-405">
                  <div className="w-7 h-7 rounded-full bg-off-white flex items-center justify-center" data-id="element-406">
                    <X size={14} weight="bold" className="text-gray-400" data-id="element-407" />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-gray-500" data-id="element-408">
                    A typical bookkeeper
                  </div>
                </div>
                <ul className="space-y-4" data-id="element-409">
                  {comparison.map((row, i) => <li key={i} className="text-sm text-gray-600 leading-relaxed flex items-start" data-id="element-410">
                      <span className="w-1 h-1 rounded-full bg-gray-400 mt-2 mr-3 flex-shrink-0" data-id="element-411"></span>
                      {row.typical}
                    </li>)}
                </ul>
              </div>
              <div className="p-7 lg:p-9 bg-near-black text-white relative overflow-hidden" data-id="element-412">
                <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-413"></div>
                <div className="relative z-10" data-id="element-414">
                  <div className="flex items-center gap-2 mb-6" data-id="element-415">
                    <div className="w-7 h-7 rounded-full bg-knight-orange/15 flex items-center justify-center" data-id="element-416">
                      <CheckCircle size={14} weight="fill" className="text-knight-orange" data-id="element-417" />
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-knight-orange" data-id="element-418">
                      With Knight
                    </div>
                  </div>
                  <ul className="space-y-4" data-id="element-419">
                    {comparison.map((row, i) => <li key={i} className="text-sm text-gray-200 leading-relaxed flex items-start" data-id="element-420">
                        <CheckCircle size={16} weight="regular" className="text-knight-orange mr-2.5 flex-shrink-0 mt-0.5" data-id="element-421" />
                        {row.knight}
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Outcomes Grid */}
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 mb-16 stagger-children" data-id="element-422">
          {outcomes.map((o, i) => <div key={i} className="flex flex-col" data-id="element-423">
              <div className="w-12 h-12 bg-white rounded-md flex items-center justify-center mb-5 border border-hairline" data-id="element-424">
                <o.icon size={24} weight="duotone" className="text-knight-orange" data-id="element-425" />
              </div>
              <h4 className="text-lg font-heading font-bold text-charcoal mb-2.5" data-id="element-426">
                {o.title}
              </h4>
              <p className="text-gray-600 leading-relaxed text-sm" data-id="element-427">{o.desc}</p>
            </div>)}
        </div>

        <div className="text-center" data-id="element-428">
          <a href="#lead" className="inline-flex items-center px-8 py-4 bg-knight-orange text-white font-semibold rounded-md hover:bg-knight-orange-hover transition-colors duration-300 ease-out-expo" data-id="element-429">
            Book a Free Consultation
            <ArrowRight size={18} weight="regular" className="ml-2" data-id="element-430" />
          </a>
          <p className="font-mono text-[11px] tracking-wide text-gray-500 uppercase mt-4" data-id="element-431">
            No startup costs / No lock-in / Fixed monthly fees / Cancel anytime
          </p>
        </div>
      </div>
    </section>;
}